import Highcharts from 'highcharts';
import HighchartsFunnel from 'highcharts/modules/funnel';
import { gantt } from 'dhtmlx-gantt';
import StructureService from './structure-service';
import FormatService from './format-service';
import ScreenService from './screen-service';
import DateService from './date-service';
import { TIMEOUT_DEFAULT } from '../constants/system/timeouts';
import { ACCENT_COLORS_CONTRAST, COLORS } from '../constants/system/colors';
import {
  DEAL_SCHEDULE_ZOOM_ITEM_MONTH, DEAL_SCHEDULE_ZOOM_ITEM_QUARTER, DEAL_SCHEDULE_ZOOM_ITEM_WEEK,
  DEAL_SCHEDULE_ZOOM_ITEM_YEAR,
} from '../constants/entity/deal-schedule-zoom-items';

export default class ChartService {
  private static HIGHCHARTS_FUNNEL_TYPE_PYRAMID: string = 'pyramid';
  private static HIGHCHARTS_FUNNEL_HEIGHT: number = 270;
  private static HIGHCHARTS_FUNNEL_LABEL_TEXT_ALIGN_CENTER: string = 'center';
  private static HIGHCHARTS_FUNNEL_ITEM_WIDTH: number = 17;
  private static HIGHCHARTS_FUNNEL_ITEM_WIDTH_LAST: number = 32;
  private static GANTT_CHART_DATE_FORMAT: string = '%m/%d/%Y %H:%i';
  private static GANTT_CHART_DATE_FORMAT_MODAL: string = '%F %j, %Y';
  private static GANTT_CHART_DATE_FORMAT_DAY: string = '%j';
  private static GANTT_CHART_DATE_FORMAT_DAY_DETAILED: string = '%j %D';
  private static GANTT_CHART_DATE_FORMAT_WEEK: string = '%d %M';
  private static GANTT_CHART_DATE_FORMAT_WEEK_DETAILED: string = '%W week';
  private static GANTT_CHART_DATE_FORMAT_MONTH: string = '%M';
  private static GANTT_CHART_DATE_FORMAT_MONTH_DETAILED: string = '%F, %Y';
  private static GANTT_CHART_DATE_FORMAT_YEAR: string = '%Y';
  private static GANTT_CHART_ITEM_ID_PARENT_ROOT: string = '0';
  private static GANTT_CHART_GRID_WIDTH: number = 400;
  private static GANTT_CHART_GRID_WIDTH_RESPONSIVE: number = 120;
  private static GANTT_CHART_COLUMN_MIN_WIDTH_DEFAULT: number = 10;
  private static GANTT_CHART_COLUMN_WIDTH_ALL: string = '*';
  private static GANTT_CHART_COLUMN_ALIGN_LEFT: string = 'left';
  private static GANTT_CHART_COLUMN_ALIGN_CENTER: string = 'center';
  private static GANTT_CHART_QUICK_INFO_TEXT_LIMIT: number = 50;
  private static GANTT_CHART_LIGHTBOX_SECTION_HEIGHT_DEFAULT: number = 45;
  private static GANTT_CHART_LIGHTBOX_SECTION_HEIGHT_EXTENDED: number = 85;
  private static GANTT_CHART_LIGHTBOX_FIELD_TYPE_TEXTAREA: string = 'textarea';
  private static GANTT_CHART_LIGHTBOX_FIELD_TYPE_PARENT: string = 'parent';
  private static GANTT_CHART_LIGHTBOX_FIELD_TYPE_SELECT: string = 'select';
  private static GANTT_CHART_LIGHTBOX_FIELD_TYPE_TIME: string = 'time';
  private static GANTT_CHART_PROGRESS_DEFAULT: number = 0;
  private static GANTT_CHART_SCALE_HEIGHT_DEFAULT: number = 60;
  private static GANTT_CHART_SCALE_STEP_DEFAULT: number = 1;
  private static GANTT_CHART_UNIT_DAY: string = 'day';
  private static GANTT_CHART_UNIT_WEEK: string = 'week';
  private static GANTT_CHART_UNIT_MONTH: string = 'month';
  private static GANTT_CHART_UNIT_YEAR: string = 'year';

  public static createFunnelChart(context: string, values: number[]): void {
    const highcharts: HighChartsInstance = Highcharts;

    if (StructureService.checkIsValueObject(highcharts) && typeof HighchartsFunnel === 'function') {
      HighchartsFunnel(highcharts);
    }

    highcharts.chart(context, {
      chart: {
        type: this.HIGHCHARTS_FUNNEL_TYPE_PYRAMID,
      },
      title: null,
      plotOptions: {
        pyramid: {
          height: this.HIGHCHARTS_FUNNEL_HEIGHT,
          colors: ACCENT_COLORS_CONTRAST,
        },
        series: {
          reversed: false,
          dataLabels: {
            useHTML: true,
            inside: true,
            style: {
              color: COLORS.white,
              textAlign: this.HIGHCHARTS_FUNNEL_LABEL_TEXT_ALIGN_CENTER,
            },
          },
          states: {
            inactive: {
              enabled: false,
            },
          },
        },
      },
      tooltip: {
        enabled: false,
      },
      series: [
        {
          data: values.map((item: number, index: number): number[] => [
            item,
            index === values.length - 1 ? this.HIGHCHARTS_FUNNEL_ITEM_WIDTH_LAST : this.HIGHCHARTS_FUNNEL_ITEM_WIDTH,
          ]),
        },
      ],
      accessibility: {
        enabled: false,
      },
    });
  }

  public static createGanttChart(
    containerId: string,
    context: HTMLElement,
    values: GanttChartData,
    zoom: string,
    isReadOnly: boolean,
    isResponsive: boolean,
    onChange: Function,
    colorList: GanttChartColorItem[],
    tableVisibleColumns: string[],
    withAddButton: boolean,
    infoModalRenderer: Function,
    taskTextRenderer: Function,
  ): GanttInstance {
    const COLUMN_NAME_NAME: string = 'name';
    const COLUMN_NAME_ASSIGNED_TO: string = 'assignedTo';
    const COLUMN_NAME_PROGRESS: string = 'progress';
    const COLUMN_NAME_START_DATE: string = 'startDate';
    const COLUMN_NAME_END_DATE: string = 'endDate';
    const COLUMN_NAME_DURATION: string = 'duration';

    const COLUMN_MIN_WIDTH_WEEK: number = 50;
    const COLUMN_MIN_WIDTH_MONTH: number = 20;
    const COLUMN_MIN_WIDTH_QUARTER: number = 50;
    const COLUMN_MIN_WIDTH_YEAR: number = 5;

    const COLUMN_MIN_WIDTH_ASSIGNED_AT: number = 100;
    const COLUMN_MIN_WIDTH_PROGRESS: number = 90;
    const COLUMN_MIN_WIDTH_START_DATE: number = 90;
    const COLUMN_MIN_WIDTH_END_DATE: number = 90;

    const zoomColumnMinWidthMap: Record<string, number> = {
      [DEAL_SCHEDULE_ZOOM_ITEM_WEEK]: COLUMN_MIN_WIDTH_WEEK,
      [DEAL_SCHEDULE_ZOOM_ITEM_MONTH]: COLUMN_MIN_WIDTH_MONTH,
      [DEAL_SCHEDULE_ZOOM_ITEM_QUARTER]: COLUMN_MIN_WIDTH_QUARTER,
      [DEAL_SCHEDULE_ZOOM_ITEM_YEAR]: COLUMN_MIN_WIDTH_YEAR,
    };

    const ganttChart: GanttInstance = gantt;
    ganttChart.config.readonly = isReadOnly;
    ganttChart.config.autofit = true;
    ganttChart.config.xml_date = this.GANTT_CHART_DATE_FORMAT;
    ganttChart.config.responsive_lightbox = true;
    ganttChart.config.grid_width = isResponsive ? this.GANTT_CHART_GRID_WIDTH_RESPONSIVE : this.GANTT_CHART_GRID_WIDTH;
    ganttChart.config.prevent_default_scroll = true;
    ganttChart.config.min_column_width = zoomColumnMinWidthMap[zoom] || this.GANTT_CHART_COLUMN_MIN_WIDTH_DEFAULT;
    ganttChart.config.start_on_monday = false;
    ganttChart.config.order_branch = true;
    ganttChart.config.order_branch_free = true;

    if (zoom !== DEAL_SCHEDULE_ZOOM_ITEM_WEEK && zoom !== DEAL_SCHEDULE_ZOOM_ITEM_QUARTER) {
      ganttChart.config.fit_tasks = true;
    }

    ganttChart.config.autosize = true;

    ganttChart.config.drag_timeline = {
      ignore: '.gantt_task_line, .gantt_task_link',
      useKey: false,
    };

    ganttChart.plugins({
      quick_info: true,
      drag_timeline: true,
    });

    ganttChart.locale.labels.section_name = 'Name';
    ganttChart.locale.labels.section_parent = 'Parent Task';
    ganttChart.locale.labels.section_assigned_to = 'Assigned To';
    ganttChart.locale.labels.section_start_date = 'Start Date';
    ganttChart.locale.labels.section_end_date = 'Due Date';
    ganttChart.locale.labels.section_notes = 'Notes';
    ganttChart.locale.labels.section_color = 'Color';

    const container: HTMLElement | null = isResponsive ? ScreenService.getElementById(containerId) : null;
    ganttChart.ext.quickInfo.setContainer(container ? container.firstChild : null);

    ganttChart.templates.quick_info_title = (start: Date, end: Date, task: GanttTask): string => (
      task.text ? task.text.substring(0, this.GANTT_CHART_QUICK_INFO_TEXT_LIMIT) : ''
    );
    ganttChart.templates.quick_info_content = (start: Date, end: Date, task: GanttTask): string => {
      const parentTask: GanttTask | null = task.parent && task.parent !== this.GANTT_CHART_ITEM_ID_PARENT_ROOT
        ? ganttChart.getTask(task.parent) : null;
      const dateStart: string = ganttChart.date.date_to_str(this.GANTT_CHART_DATE_FORMAT_MODAL)(start);
      const dateEnd: string = ganttChart.date.date_to_str(this.GANTT_CHART_DATE_FORMAT_MODAL)(end);
      const taskAssignedTo: string | null = task.assigned_to || null;
      const taskParentName: string | null = parentTask ? parentTask.text : null;
      const taskNotes: string | null = task.notes || null;

      return infoModalRenderer ? infoModalRenderer(dateStart, dateEnd, taskAssignedTo, taskParentName, taskNotes) : '';
    };
    ganttChart.templates.task_text = (start: Date, end: Date, task: GanttTask): string | null => (
      taskTextRenderer ? taskTextRenderer(task.text) : task.text
    );

    ganttChart.config.columns = [];

    if (tableVisibleColumns.includes(COLUMN_NAME_NAME)) {
      ganttChart.config.columns.push({
        name: 'text',
        label: 'Task name',
        tree: true,
        width: this.GANTT_CHART_COLUMN_WIDTH_ALL,
        align: this.GANTT_CHART_COLUMN_ALIGN_LEFT,
        min_width: isResponsive ? 0 : 200,
        template: (task: GanttTask): string | null => (
          taskTextRenderer ? taskTextRenderer(task.text) : task.text
        ),
      });
    }

    if (!isResponsive) {
      if (tableVisibleColumns.includes(COLUMN_NAME_ASSIGNED_TO)) {
        ganttChart.config.columns.push({
          name: 'assigned_to',
          label: 'Assigned To',
          align: this.GANTT_CHART_COLUMN_ALIGN_CENTER,
          min_width: COLUMN_MIN_WIDTH_ASSIGNED_AT,
        });
      }

      if (tableVisibleColumns.includes(COLUMN_NAME_PROGRESS)) {
        ganttChart.config.columns.push({
          name: 'progress',
          label: '% Complete',
          align: this.GANTT_CHART_COLUMN_ALIGN_CENTER,
          min_width: COLUMN_MIN_WIDTH_PROGRESS,
          template: (task: GanttTask): string => (
            `${task.progress ? Math.floor(task.progress * 100) : 0}`
          ),
        });
      }

      if (tableVisibleColumns.includes(COLUMN_NAME_START_DATE)) {
        ganttChart.config.columns.push({
          name: 'start_date',
          label: 'Start Date',
          align: this.GANTT_CHART_COLUMN_ALIGN_CENTER,
          min_width: COLUMN_MIN_WIDTH_START_DATE,
        });
      }

      if (tableVisibleColumns.includes(COLUMN_NAME_END_DATE)) {
        ganttChart.config.columns.push({
          name: 'end_date',
          label: 'Due Date',
          align: this.GANTT_CHART_COLUMN_ALIGN_CENTER,
          min_width: COLUMN_MIN_WIDTH_END_DATE,
        });
      }

      if (tableVisibleColumns.includes(COLUMN_NAME_DURATION)) {
        ganttChart.config.columns.push({
          name: 'duration',
          label: 'Duration',
          align: this.GANTT_CHART_COLUMN_ALIGN_CENTER,
        });
      }
    }

    if (!isReadOnly && withAddButton) {
      ganttChart.config.columns = [...ganttChart.config.columns, {
        name: 'add',
        label: '',
      }];
    }

    ganttChart.config.lightbox.sections = [
      {
        name: 'name',
        map_to: 'text',
        height: this.GANTT_CHART_LIGHTBOX_SECTION_HEIGHT_DEFAULT,
        type: this.GANTT_CHART_LIGHTBOX_FIELD_TYPE_TEXTAREA,
        focus: true,
      },
      {
        name: 'parent',
        map_to: 'parent',
        height: this.GANTT_CHART_LIGHTBOX_SECTION_HEIGHT_DEFAULT,
        type: this.GANTT_CHART_LIGHTBOX_FIELD_TYPE_PARENT,
        allow_root: FormatService.BOOLEAN_VALUE_TRUE,
        root_label: 'None',
      },
      {
        name: 'assigned_to',
        map_to: 'assigned_to',
        height: this.GANTT_CHART_LIGHTBOX_SECTION_HEIGHT_DEFAULT,
        type: this.GANTT_CHART_LIGHTBOX_FIELD_TYPE_TEXTAREA,
      },
      {
        name: 'start_date',
        map_to: 'start_date',
        height: this.GANTT_CHART_LIGHTBOX_SECTION_HEIGHT_DEFAULT,
        type: this.GANTT_CHART_LIGHTBOX_FIELD_TYPE_TIME,
        single_date: true,
      },
      {
        name: 'end_date',
        map_to: 'end_date',
        height: this.GANTT_CHART_LIGHTBOX_SECTION_HEIGHT_DEFAULT,
        type: this.GANTT_CHART_LIGHTBOX_FIELD_TYPE_TIME,
        single_date: true,
      },
      {
        name: 'notes',
        map_to: 'notes',
        height: this.GANTT_CHART_LIGHTBOX_SECTION_HEIGHT_EXTENDED,
        type: this.GANTT_CHART_LIGHTBOX_FIELD_TYPE_TEXTAREA,
      },
      {
        name: 'color',
        map_to: 'color',
        height: this.GANTT_CHART_LIGHTBOX_SECTION_HEIGHT_EXTENDED,
        type: this.GANTT_CHART_LIGHTBOX_FIELD_TYPE_SELECT,
        options: ganttChart.serverList(
          'colorList',
          colorList && colorList.length ? colorList.map((color: GanttChartColorItem): GanttServerListItem => ({
            key: color.name,
            label: color.title,
          })) : [],
        ),
      },
    ];

    ganttChart.config.links = {
      finish_to_start: 'finish-to-start',
      start_to_start: 'start-to-start',
      finish_to_finish: 'finish-to-finish',
      start_to_finish: 'start-to-finish',
    };

    ganttChart.ext.zoom.init({
      levels: [
        {
          name: 'week',
          scale_height: this.GANTT_CHART_SCALE_HEIGHT_DEFAULT,
          scales: [
            {
              unit: this.GANTT_CHART_UNIT_WEEK,
              step: this.GANTT_CHART_SCALE_STEP_DEFAULT,
              format: (date: Date): string => {
                const dateToStringCallback: Function = ganttChart.date.date_to_str(this.GANTT_CHART_DATE_FORMAT_WEEK);
                const endDate: Date = ganttChart.date.add(
                  date,
                  +(DateService.WEEK_DAYS_TOTAL - 1),
                  this.GANTT_CHART_UNIT_DAY,
                );

                return `${dateToStringCallback(date)} - ${dateToStringCallback(endDate)}`;
              },
            },
            {
              unit: this.GANTT_CHART_UNIT_DAY,
              step: this.GANTT_CHART_SCALE_STEP_DEFAULT,
              format: this.GANTT_CHART_DATE_FORMAT_DAY_DETAILED,
            },
          ],
        },
        {
          name: 'month',
          scale_height: this.GANTT_CHART_SCALE_HEIGHT_DEFAULT,
          scales: [
            {
              unit: this.GANTT_CHART_UNIT_MONTH,
              format: this.GANTT_CHART_DATE_FORMAT_MONTH_DETAILED,
              step: this.GANTT_CHART_SCALE_STEP_DEFAULT,
            },
            {
              unit: this.GANTT_CHART_UNIT_DAY,
              format: this.GANTT_CHART_DATE_FORMAT_DAY,
              step: this.GANTT_CHART_SCALE_STEP_DEFAULT,
            },
          ],
        },
        {
          name: 'quarter',
          scale_height: this.GANTT_CHART_SCALE_HEIGHT_DEFAULT,
          scales: [
            {
              unit: this.GANTT_CHART_UNIT_MONTH,
              step: this.GANTT_CHART_SCALE_STEP_DEFAULT,
              format: this.GANTT_CHART_DATE_FORMAT_MONTH,
            },
            {
              unit: this.GANTT_CHART_UNIT_WEEK,
              step: this.GANTT_CHART_SCALE_STEP_DEFAULT,
              format: this.GANTT_CHART_DATE_FORMAT_WEEK_DETAILED,
            },
          ],
        },
        {
          name: 'year',
          scale_height: this.GANTT_CHART_SCALE_HEIGHT_DEFAULT,
          scales: [
            {
              unit: this.GANTT_CHART_UNIT_YEAR,
              step: this.GANTT_CHART_SCALE_STEP_DEFAULT,
              format: this.GANTT_CHART_DATE_FORMAT_YEAR,
            },
            {
              unit: this.GANTT_CHART_UNIT_MONTH,
              step: this.GANTT_CHART_SCALE_STEP_DEFAULT,
              format: this.GANTT_CHART_DATE_FORMAT_MONTH,
            },
          ],
        },
      ],
    });

    ganttChart.eventList = [
      ganttChart.attachEvent('onAfterTaskAdd', (): void => {
        onChange(ganttChart.serialize());
      }),
      ganttChart.attachEvent('onAfterTaskUpdate', (): void => {
        onChange(ganttChart.serialize());
      }),
      ganttChart.attachEvent('onAfterTaskDelete', (): void => {
        onChange(ganttChart.serialize());
      }),
      ganttChart.attachEvent('onAfterLinkAdd', (): void => {
        onChange(ganttChart.serialize());
      }),
      ganttChart.attachEvent('onAfterLinkUpdate', (): void => {
        onChange(ganttChart.serialize());
      }),
      ganttChart.attachEvent('onAfterLinkDelete', (): void => {
        onChange(ganttChart.serialize());
      }),
      ganttChart.attachEvent('onRowDragEnd', (): void => {
        onChange(ganttChart.serialize());
      }),
    ];

    ganttChart.attachEvent('onLightboxSave', (): boolean => {
      return true;
    });

    ganttChart.attachEvent('onGanttReady', (): void => {
      ganttChart.config.buttons_right = ['gantt_save_btn', 'gantt_cancel_btn'];
      ganttChart.config.buttons_left = ['gantt_delete_btn'];
    });

    ganttChart.attachEvent('onLightbox', (): void => {
      setTimeout((): void => {
        ganttChart.ext.quickInfo.hide();
      }, TIMEOUT_DEFAULT);
    });

    ganttChart.attachEvent('onTaskClick', (task: GanttTask, event: React.PointerEvent): boolean => {
      if (event.target && ((event.target as HTMLElement).classList.contains('gantt_open')
        || (event.target as HTMLElement).classList.contains('gantt_close'))) {
        setTimeout((): void => {
          ganttChart.ext.quickInfo.hide();
        }, TIMEOUT_DEFAULT);
      }

      return true;
    });

    if (zoom) {
      ganttChart.ext.zoom.setLevel(zoom);
    }

    setTimeout((): void => {
      ganttChart.init(context);
      ganttChart.clearAll();
      ganttChart.parse(values);
    }, TIMEOUT_DEFAULT);

    return ganttChart;
  }

  public static getGanttChartProgressDefault(): number {
    return this.GANTT_CHART_PROGRESS_DEFAULT;
  }

  public static detachAllGanttChartEventListeners(): void {
    const ganttChart: GanttInstance = gantt;

    if (ganttChart && ganttChart.eventList) {
      for (let eventIndex: number = 0; eventIndex < ganttChart.eventList.length; eventIndex++) {
        ganttChart.detachEvent(ganttChart.eventList[eventIndex]);
      }

      ganttChart.eventList = [];
    }
  }
}
